/**
 * WHAT: Date/time formatting utilities that convert UTC backend timestamps to the user's local timezone.
 * WHY:  The Django backend stores all timestamps in UTC. Every date shown in the DAMA UI — campaign
 *       deadlines, refresh times, attestation timestamps — must be converted to the user's local
 *       timezone with a visible abbreviation (EST, PST, etc.) for clarity and audit accuracy.
 *
 * STANDARD: All dates displayed in the UI should use these functions to ensure:
 * - Consistent formatting across the application
 * - Proper timezone conversion (UTC to user's local timezone)
 * - Timezone abbreviation displayed (EST, CST, PST, etc.)
 *
 * Usage:
 *   import { formatDateTime, formatDate, formatDateShort } from '../utils/dateUtils';
 *
 *   // Full datetime with timezone: "01/15/2026, 10:30:45 AM EST"
 *   formatDateTime(timestamp)
 *
 *   // Date only: "01/15/2026"
 *   formatDate(timestamp)
 *
 *   // Short date: "Jan 15, 2026"
 *   formatDateShort(timestamp)
 *
 *   // Compact (no timezone): "01/15/2026, 10:30 AM"
 *   formatDateTimeCompact(timestamp)
 */

/**
 * Parse a timestamp string into a Date object
 * Handles multiple formats from the backend:
 * - "2025-10-28 01:26:24.071836" (Django format, assumed UTC)
 * - "2025-10-28T01:26:24.071836Z" (ISO format with Z suffix)
 * - "2025-10-28T01:26:24.071836+00:00" (ISO format with timezone offset)
 * - "2025-10-28" (date only)
 *
 * @param {string} timestamp - Timestamp string from backend
 * @returns {Date|null} Parsed Date object or null if invalid
 */
const parseTimestamp = (timestamp) => {
  if (!timestamp) return null;

  try {
    let dateStr = String(timestamp).trim();

    // If already has timezone info (Z or +/-offset), parse directly
    if (dateStr.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(dateStr)) {
      const date = new Date(dateStr);
      return isNaN(date.getTime()) ? null : date;
    }

    // Replace space with T for ISO format compatibility
    if (dateStr.includes(' ') && !dateStr.includes('T')) {
      dateStr = dateStr.replace(' ', 'T');
    }

    // Append Z to indicate UTC if no timezone specified
    if (!dateStr.endsWith('Z')) {
      dateStr = dateStr + 'Z';
    }

    const date = new Date(dateStr);
    return isNaN(date.getTime()) ? null : date;
  } catch (error) {
    console.error('Error parsing timestamp:', error);
    return null;
  }
};

/**
 * WHAT: Formats a UTC timestamp to local datetime with timezone abbreviation.
 * WHY:  Primary display format for refresh timestamps, attestation dates, and campaign events
 *       where users need to know the exact time and timezone for audit purposes.
 *
 * @param {string} utcTimestamp - UTC timestamp string from backend
 * @param {boolean} includeSeconds - Whether to include seconds in the output
 * @returns {string} Formatted datetime string in local timezone with timezone abbreviation
 */
export const formatDateTime = (utcTimestamp, includeSeconds = false) => {
  if (!utcTimestamp) return '-';

  try {
    const date = parseTimestamp(utcTimestamp);
    if (!date) return String(utcTimestamp);

    const options = {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZoneName: 'short'
    };

    if (includeSeconds) {
      options.second = '2-digit';
    }

    return date.toLocaleString('en-US', options);
  } catch (error) {
    console.error('Error formatting datetime:', error);
    return String(utcTimestamp);
  }
};

/**
 * WHAT: Formats a UTC timestamp to date only (MM/DD/YYYY), no time component.
 * WHY:  Used for campaign start/end dates, user creation dates, and other date-only fields
 *       where showing the time would add noise without value.
 *
 * @param {string} utcTimestamp - UTC timestamp string from backend
 * @returns {string} Formatted date string (MM/DD/YYYY)
 */
export const formatDate = (utcTimestamp) => {
  if (!utcTimestamp) return '-';

  try {
    const date = parseTimestamp(utcTimestamp);
    if (!date) return String(utcTimestamp);

    const options = {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    };

    return date.toLocaleDateString('en-US', options);
  } catch (error) {
    console.error('Error formatting date:', error);
    return String(utcTimestamp);
  }
};

/**
 * WHAT: Formats a UTC timestamp to a human-friendly short date (e.g., "Jan 15, 2026").
 * WHY:  Used in card headers and summary views where a compact, readable date is preferable
 *       to the numeric MM/DD/YYYY format.
 *
 * @param {string} utcTimestamp - UTC timestamp string from backend
 * @returns {string} Formatted date string (Mon DD, YYYY)
 */
export const formatDateShort = (utcTimestamp) => {
  if (!utcTimestamp) return '-';

  try {
    const date = parseTimestamp(utcTimestamp);
    if (!date) return String(utcTimestamp);

    const options = {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    };

    return date.toLocaleDateString('en-US', options);
  } catch (error) {
    console.error('Error formatting date short:', error);
    return String(utcTimestamp);
  }
};

/**
 * WHAT: Formats a UTC timestamp to time only with timezone (e.g., "10:30 AM EST").
 * WHY:  Used alongside date columns in refresh history tables where the date is shown
 *       separately and only the time portion is needed.
 *
 * @param {string} utcTimestamp - UTC timestamp string from backend
 * @param {boolean} includeSeconds - Whether to include seconds
 * @returns {string} Formatted time string with timezone
 */
export const formatTime = (utcTimestamp, includeSeconds = false) => {
  if (!utcTimestamp) return '-';

  try {
    const date = parseTimestamp(utcTimestamp);
    if (!date) return String(utcTimestamp);

    const options = {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZoneName: 'short'
    };

    if (includeSeconds) {
      options.second = '2-digit';
    }

    return date.toLocaleTimeString('en-US', options);
  } catch (error) {
    console.error('Error formatting time:', error);
    return String(utcTimestamp);
  }
};

/**
 * WHAT: Converts a UTC timestamp to a relative time string (e.g., "2 hours ago").
 * WHY:  Shown next to recent activity indicators (last refresh, last attestation) to give
 *       users an at-a-glance sense of recency without mental date arithmetic.
 * @param {string} utcTimestamp - UTC timestamp string from backend
 * @returns {string} Relative time string
 */
export const formatRelativeTime = (utcTimestamp) => {
  if (!utcTimestamp) return '-';

  try {
    const date = parseTimestamp(utcTimestamp);
    if (!date) return String(utcTimestamp);

    const now = new Date();
    const diffMs = now - date;
    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffSecs / 60);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffSecs < 60) {
      return 'just now';
    } else if (diffMins < 60) {
      return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
    } else if (diffHours < 24) {
      return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    } else if (diffDays < 30) {
      return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    } else {
      return formatDate(utcTimestamp);
    }
  } catch (error) {
    console.error('Error formatting relative time:', error);
    return String(utcTimestamp);
  }
};

/**
 * WHAT: Converts a duration in seconds to a human-readable string (e.g., "2m 30s", "1h 15m").
 * WHY:  Database refresh execution times are stored in seconds; this formats them for display
 *       in the refresh history and execution details panels.
 * @param {number} seconds - Duration in seconds
 * @returns {string} Formatted duration string
 */
export const formatDuration = (seconds) => {
  if (seconds === null || seconds === undefined) return '-';

  if (seconds < 1) {
    return `${Math.round(seconds * 1000)}ms`;
  } else if (seconds < 60) {
    return `${seconds.toFixed(1)}s`;
  } else if (seconds < 3600) {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}m ${secs}s`;
  } else {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${mins}m`;
  }
};

/**
 * WHAT: Returns the user's current timezone abbreviation (e.g., "EST", "PST").
 * WHY:  Displayed in column headers and report footers so users know what timezone
 *       the displayed times are relative to.
 * @returns {string} Timezone name (e.g., "EST" or "EDT")
 */
export const getCurrentTimezone = () => {
  try {
    const now = new Date();
    const timezone = now.toLocaleTimeString('en-US', { timeZoneName: 'short' }).split(' ').pop();
    return timezone;
  } catch (error) {
    return 'Local';
  }
};

/**
 * WHAT: Formats a UTC timestamp to a compact datetime without timezone (e.g., "01/15/2026, 10:30 AM").
 * WHY:  Used in dense table cells where column width is limited and the timezone is already
 *       shown in the column header via getCurrentTimezone().
 *
 * @param {string} utcTimestamp - UTC timestamp string from backend
 * @returns {string} Compact formatted datetime string (no timezone)
 */
export const formatDateTimeCompact = (utcTimestamp) => {
  if (!utcTimestamp) return '-';

  try {
    const date = parseTimestamp(utcTimestamp);
    if (!date) return String(utcTimestamp);

    // Format: MM/DD/YYYY, HH:MM AM/PM (no timezone)
    const options = {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    };

    return date.toLocaleString('en-US', options);
  } catch (error) {
    console.error('Error formatting datetime compact:', error);
    return String(utcTimestamp);
  }
};

/**
 * WHAT: Formats a UTC timestamp for report/export display with full precision and timezone.
 * WHY:  PDF and CSV exports need a complete, unambiguous datetime for audit evidence;
 *       this format includes seconds and timezone to satisfy compliance review requirements.
 *
 * @param {string} utcTimestamp - UTC timestamp string from backend
 * @returns {string} Formatted datetime for reports
 */
export const formatDateTimeForReport = (utcTimestamp) => {
  if (!utcTimestamp) return '-';

  try {
    const date = parseTimestamp(utcTimestamp);
    if (!date) return String(utcTimestamp);

    const options = {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
      timeZoneName: 'short'
    };

    return date.toLocaleString('en-US', options);
  } catch (error) {
    console.error('Error formatting datetime for report:', error);
    return String(utcTimestamp);
  }
};
